import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { userAppContext } from '../../context/Context.tsx';
import {
  canExtendCommercialTrial,
  DEMO_BOOKED_TAG,
  hasBookedDemo,
  isEligibleForCommercialTrialExtension,
  isTrialExpired,
  mergeTrialAttributes,
} from '../../utils/trialUtils';
import TrialExpiredModal from '../User/TrialExpiredModal.tsx';
import ReviewingApplicationModal from '../User/ReviewingApplicationModal.tsx';
import BookDemoEmailModal from '../NavMenus/BookDemoEmailModal.tsx';
import { approveBookDemoEmail } from '../NavMenus/bookDemoSession';
import AcademicApplicationService from '../../services/AcademicApplicationService.ts';
import { refreshSession } from '../../services/RefreshSession';
import { useAuthStore } from '../../store/authStore';
import { enrichUserWithCognitoAttributes } from '../../utils/enrichUserWithCognitoAttributes';
import { logTrialDebug, logTrialEvaluation } from '../../utils/trialDebug';

const GlobalTrialBanner = () => {
  const { groups, user } = userAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const userEmail = user?.email ?? null;
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('trial');
  const [modalDismissed, setModalDismissed] = useState(false);
  const [academicStatus, setAcademicStatus] = useState(null);
  const [trialType, setTrialType] = useState(null);
  const [trialAttributes, setTrialAttributes] = useState(null);
  const [activeCampaignTags, setActiveCampaignTags] = useState<string[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusLoadedForEmail, setStatusLoadedForEmail] = useState<string | null>(null);
  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(false);
  const isOnBookDemoPage = location.pathname === '/book-a-demo';
  const shouldSuppressTrialModal = isBookDemoModalOpen || isOnBookDemoPage;

  useEffect(() => {
    setShowModal(false);
    setModalType('trial');
    setModalDismissed(false);
    setAcademicStatus(null);
    setTrialType(null);
    setTrialAttributes(null);
    setActiveCampaignTags([]);
    setLoadingStatus(false);
    setStatusLoadedForEmail(null);
    setIsBookDemoModalOpen(false);
  }, [userEmail]);

  const applyAcademicStatusResponse = (
    response: Awaited<ReturnType<typeof AcademicApplicationService.getAcademicStatus>>,
  ) => {
    setAcademicStatus(response.status);
    setTrialType(response.trialType);
    setActiveCampaignTags((response.activeCampaignTags ?? []).map(tag => String(tag)));

    const nextTrialAttributes = {
      createdAt: response.createdAt ?? null,
      extendedTrialDays: response.extendedTrialDays != null ? response.extendedTrialDays : null,
      trialExtendedAt: response.trialExtendedAt ?? null,
    };
    setTrialAttributes(nextTrialAttributes);
    useAuthStore.getState().setTrialAttributes(nextTrialAttributes);
    useAuthStore.getState().setTrialAttributesLoaded(true);
    const currentUser = useAuthStore.getState().user;
    if (currentUser) {
      useAuthStore.getState().setUser(mergeTrialAttributes(currentUser, nextTrialAttributes));
    }
    if (response.trialType || response.userType) {
      useAuthStore.getState().setUserType(response.trialType || response.userType || null);
    }
  };

  useEffect(() => {
    if (!userEmail) {
      return;
    }

    let cancelled = false;

    const checkAcademicStatus = async () => {
      setLoadingStatus(true);
      try {
        const response = await AcademicApplicationService.getAcademicStatus(userEmail);
        if (cancelled) {
          return;
        }

        const contextRoles = groups || [];
        const actualRoles = response.groups || [];

        const roleChanged =
          (actualRoles.includes('ROLE_ACADEMIC') && !contextRoles.includes('ROLE_ACADEMIC')) ||
          (actualRoles.includes('ROLE_STANDARD') && !contextRoles.includes('ROLE_STANDARD')) ||
          (actualRoles.includes('ROLE_ADVANCED') && !contextRoles.includes('ROLE_ADVANCED')) ||
          (contextRoles.includes('ROLE_TRIAL') && !actualRoles.includes('ROLE_TRIAL'));

        if (roleChanged) {
          const reloadKey = `role-reload-${userEmail}`;
          const hasReloaded = sessionStorage.getItem(reloadKey);
          const reloadTimestamp = sessionStorage.getItem(`${reloadKey}-timestamp`);

          if (reloadTimestamp) {
            const timeSinceReload = Date.now() - parseInt(reloadTimestamp);
            if (timeSinceReload > 5 * 60 * 1000) {
              sessionStorage.removeItem(reloadKey);
              sessionStorage.removeItem(`${reloadKey}-timestamp`);
            }
          }

          if (!hasReloaded) {
            sessionStorage.setItem(reloadKey, 'true');
            sessionStorage.setItem(`${reloadKey}-timestamp`, Date.now().toString());

            try {
              await refreshSession({ forceRefresh: true });
              window.location.reload();
            } catch (error) {
              console.error('Error refreshing session:', error);
              window.location.reload();
            }
            return;
          } else {
            sessionStorage.removeItem(reloadKey);
            sessionStorage.removeItem(`${reloadKey}-timestamp`);
          }
        }

        applyAcademicStatusResponse(response);
        setStatusLoadedForEmail(userEmail);

        logTrialDebug('GlobalTrialBanner /academic-status response', {
          email: userEmail,
          status: response.status,
          trialType: response.trialType ?? null,
          createdAt: response.createdAt ?? null,
          extendedTrialDays: response.extendedTrialDays ?? null,
          trialExtendedAt: response.trialExtendedAt ?? null,
          hasBookedDemo: hasBookedDemo(response.activeCampaignTags),
          demoBookedTag: DEMO_BOOKED_TAG,
          activeCampaignTags: response.activeCampaignTags ?? [],
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error checking academic status:', error);
        setAcademicStatus(null);
        setTrialType(null);
        setActiveCampaignTags([]);
        setTrialAttributes(null);
        setStatusLoadedForEmail(userEmail);
      } finally {
        if (!cancelled) {
          setLoadingStatus(false);
        }
      }
    };

    checkAcademicStatus();

    return () => {
      cancelled = true;
    };
  }, [groups, userEmail]);

  useEffect(() => {
    if (!groups?.includes('ROLE_TRIAL') || !userEmail || academicStatus !== 'pending') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(userEmail);

        if (response.groups?.includes('ROLE_ACADEMIC')) {
          clearInterval(pollInterval);
          window.location.reload();
        }
      } catch (error) {
        console.error('Error polling academic status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [groups, userEmail, academicStatus]);

  useEffect(() => {
    if (!groups?.includes('ROLE_TRIAL') || !userEmail || modalType !== 'book-demo-for-extension') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await AcademicApplicationService.getAcademicStatus(userEmail);
        applyAcademicStatusResponse(response);

        if (hasBookedDemo(response.activeCampaignTags)) {
          clearInterval(pollInterval);
          setModalType('extension');
        }
      } catch (error) {
        console.error('Error polling demo booked status:', error);
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [groups, userEmail, modalType]);

  useEffect(() => {
    if (groups?.includes('ROLE_TRIAL') && userEmail) {
      if (loadingStatus || statusLoadedForEmail !== userEmail) {
        return;
      }
    }

    let showModalTimeout: ReturnType<typeof setTimeout> | undefined;

    if (groups?.includes('ROLE_TRIAL') && user) {
      const dismissKey = 'reviewing-application-dismissed';
      const dismissTimestamp = localStorage.getItem(dismissKey);
      let reviewingModalDismissed = false;

      if (dismissTimestamp) {
        const daysSinceDismiss = (Date.now() - parseInt(dismissTimestamp)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 7) {
          reviewingModalDismissed = true;
        } else {
          localStorage.removeItem(dismissKey);
        }
      }

      const trialUser = mergeTrialAttributes(user, trialAttributes ?? undefined);
      const expired = isTrialExpired(trialUser);
      const eligibleForExtension = isEligibleForCommercialTrialExtension(trialUser, trialType);
      const canExtend = canExtendCommercialTrial(trialUser, trialType, activeCampaignTags);

      logTrialEvaluation('GlobalTrialBanner modal decision', {
        user,
        trialAttributes,
        trialType,
        extra: {
          pathname: location.pathname,
          academicStatus,
          modalDismissed,
          expired,
          eligibleForExtension,
          canExtend,
          hasBookedDemo: hasBookedDemo(activeCampaignTags),
          activeCampaignTags,
          modalTypeWillBe:
            academicStatus === 'pending' && !reviewingModalDismissed
              ? 'reviewing'
              : expired && !modalDismissed
                ? eligibleForExtension
                  ? canExtend
                    ? 'extension'
                    : 'book-demo-for-extension'
                  : 'trial'
                : 'none',
        },
      });

      if (academicStatus === 'pending' && !reviewingModalDismissed) {
        setModalType('reviewing');
        showModalTimeout = setTimeout(() => setShowModal(true), 500);
      } else if (expired && !modalDismissed && !shouldSuppressTrialModal) {
        if (eligibleForExtension) {
          setModalType(canExtend ? 'extension' : 'book-demo-for-extension');
        } else {
          setModalType('trial');
        }
        showModalTimeout = setTimeout(() => setShowModal(true), 500);
      } else {
        setShowModal(false);
      }
    } else {
      setShowModal(false);
    }

    return () => {
      if (showModalTimeout) {
        clearTimeout(showModalTimeout);
      }
    };
  }, [
    groups,
    user,
    userEmail,
    modalDismissed,
    academicStatus,
    loadingStatus,
    statusLoadedForEmail,
    trialType,
    trialAttributes,
    activeCampaignTags,
    location.pathname,
    shouldSuppressTrialModal,
  ]);

  const refreshUserAttributes = async () => {
    const currentUser = await getCurrentUser();
    const session = await refreshSession({ forceRefresh: true });
    const userInfo = session.tokens.idToken.payload;

    const userWithAttributes = await enrichUserWithCognitoAttributes({
      ...userInfo,
      ...currentUser,
    });

    logTrialDebug('GlobalTrialBanner.refreshUserAttributes', {
      jwtAfterForceRefresh: userInfo,
      mergedUser: userWithAttributes,
    });

    useAuthStore.getState().setUser(userWithAttributes);
    return userWithAttributes;
  };

  const handleExtendTrial = async () => {
    if (!user?.email) {
      throw new Error('User email not found');
    }

    const response = await AcademicApplicationService.extendCommercialTrial(user.email);

    if (response.status === 'error') {
      throw new Error(response.message);
    }

    await refreshUserAttributes();
    await useAuthStore.getState().fetchUserType();
    window.location.reload();
  };

  const handleOpenBookDemo = () => {
    setShowModal(false);
    setIsBookDemoModalOpen(true);
  };

  const handleProceedToBookDemo = (email: string) => {
    approveBookDemoEmail(email);
    setIsBookDemoModalOpen(false);
    setShowModal(false);
    navigate('/book-a-demo');
  };

  const handleCloseBookDemoModal = () => {
    setIsBookDemoModalOpen(false);

    if (groups?.includes('ROLE_TRIAL') && user && !modalDismissed && !isOnBookDemoPage) {
      const trialUser = mergeTrialAttributes(user, trialAttributes ?? undefined);
      if (isTrialExpired(trialUser)) {
        setShowModal(true);
      }
    }
  };

  const handleCloseModal = () => {
    if (modalType === 'reviewing') {
      const dismissKey = 'reviewing-application-dismissed';
      localStorage.setItem(dismissKey, Date.now().toString());
    }
    setModalDismissed(true);
    setShowModal(false);
  };

  return (
    <>
      {showModal && modalType === 'reviewing' && <ReviewingApplicationModal onClose={handleCloseModal} />}

      {showModal && modalType !== 'reviewing' && (
        <TrialExpiredModal
          onClose={handleCloseModal}
          variant={
            modalType === 'extension'
              ? 'extension'
              : modalType === 'book-demo-for-extension'
                ? 'book-demo-for-extension'
                : 'expired'
          }
          onExtend={modalType === 'extension' ? handleExtendTrial : undefined}
          onBookDemo={modalType === 'book-demo-for-extension' ? handleOpenBookDemo : undefined}
        />
      )}

      <BookDemoEmailModal
        isOpen={isBookDemoModalOpen}
        onClose={handleCloseBookDemoModal}
        onProceed={handleProceedToBookDemo}
      />
    </>
  );
};

export default GlobalTrialBanner;
